import { PreviewTheme } from '../../modules/page-content/page-builder/PageBuilder.d'

/**
 * Capitalises the first letter of a string
 * ("string") => "String"
 */
export function capitalise(string: string): string {
    return string[0].toUpperCase() + string.slice(1)
}

/**
 * Converts a camelCase string to a label with spaces
 * ("someLabel") => "Some label
 */
export function labelify(string: string): string {
    return capitalise(string.replace(/([a-z])([A-Z])/g, '$1 $2'))
}

/**
 * Wraps a number in a currency string
 * formatted for the given locale and currency
 * (1234.56, 'en-GB', 'GBP') => "£1,234.56"
 */
export function wrapCurrency(
    number: number,
    locale = 'en-GB',
    currency = 'GBP'
) {
    return number?.toLocaleString(locale, {
        style: 'currency',
        currency,
    })
}

export const getLocalStorageValue = (key: string, defaultValue: any) => {
    const saved = localStorage.getItem(key)
    const initial = JSON.parse(saved)
    return initial || defaultValue
}

export const setSettingsValue = (key: string, value: any) => {
    const settings = getLocalStorageValue('settings', {})

    localStorage.setItem(
        'settings',
        JSON.stringify({
            ...settings,
            [key]: value,
        })
    )
}

export const removeSettingsValue = (key: string) => {
    const settings = getLocalStorageValue('settings', {})

    delete settings[key]

    localStorage.setItem('settings', JSON.stringify(settings))
}

export const getDefaultPreviewTheme = (): PreviewTheme => {
    const style = getComputedStyle(document.body)

    return {
        colors: {
            primary: {
                rgb: style.getPropertyValue('--bs-primary-rgb'),
                hex: style.getPropertyValue('--bs-primary'),
                alphaValue: 0.75,
            },
            secondary: {
                rgb: style.getPropertyValue('--bs-secondary-rgb'),
                hex: style.getPropertyValue('--bs-secondary'),
                alphaValue: 0.25,
            },
        },
    }
}

/**
 * When using Fontawesome v6 replace 'fas' with 'fa-solid' and so on..
 */
export const mapIconClass = (
    iconClass: string = '',
    defaultIcon: string = 'fa-circle'
): string => {
    return (
        'fa-solid ' +
        (iconClass !== ''
            ? 'fa-' +
              iconClass
                  .replace('fas fa-', '')
                  .replace('fa fa-fw fa-', '')
                  .replace('fa fa-', '')
                  .replace('dashboard', 'gauge-high')
                  .replace('money', 'money-bill')
                  .replace('telegram', 'brands fa-telegram')
                  .replace('hand-o-up', 'hand-point-up')
                  .replace('stack-overflow', 'brands fa-stack-overflow')
                  .replace('id-card-o', 'id-card')
                  .replace('user-circle-o', 'user-circle')
                  .replace('building-o', 'building')
                  .replace('calendar-check-o', 'calendar-check')
                  .replace('pencil-square-o', 'pencil-square')
                  .replace('sticky-note-o', 'sticky-note')
                  .replace('envelope-o', 'envelope')
            : defaultIcon)
    )
}

export const getFriendlyErrorFromErros = (errors: []): string => {
    return errors?.length > 0 ? errors.join('<br />') : 'An error occurred!'
}

export const getAxiosFriendlyError = (error: any): string => {
    let errorMsg = error.message

    try {
        errorMsg = getFriendlyErrorFromErros(error.response.data.errors)
    } catch (error) {
        console.log(error)
    }

    return errorMsg || 'An error occurred!'
}

export const getTranslation = (
    translations: object[],
    translationCode: string,
    locale: string
): string => {
    return translations[locale]?.hasOwnProperty(translationCode)
        ? translations[locale][translationCode]
        : translationCode
}

type TranslationCodeObject = {
    key: string
    code: string
}
export const getGroupTranslation = (
    translations: object[],
    translationCodes: TranslationCodeObject[],
    locale: string
): TranslationCodeObject[] => {
    const groupTranslation = []

    for (const i in translationCodes) {
        const t = translationCodes[i]

        groupTranslation[t.key] = translations[locale]?.hasOwnProperty(t.code)
            ? translations[locale][t.code]
            : ''
    }

    return groupTranslation
}
